import React, { createContext, useContext, useState, useEffect } from 'react';
import en from '../locales/en.json';
import pl from '../locales/pl.json';

const translations = { en, pl };

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem('gym_diary_lang') || localStorage.getItem('gymghost_lang');
      if (saved === 'pl' || saved === 'en') return saved;
      if (typeof navigator !== 'undefined') {
        const browserLang = (navigator.language || (navigator.languages && navigator.languages[0]) || '').toLowerCase();
        if (browserLang.startsWith('pl')) return 'pl';
      }
    } catch (e) {}
    return 'en';
  });

  const changeLanguage = (newLang) => {
    if (newLang !== 'en' && newLang !== 'pl') return;
    setLangState(newLang);
    try {
      localStorage.setItem('gym_diary_lang', newLang);
      localStorage.setItem('gymghost_lang', newLang);
      window.dispatchEvent(new CustomEvent('language_change', { detail: newLang }));
    } catch (e) {
      console.warn("Offline storage note:", e.message);
    }
  };

  const toggleLanguage = () => {
    changeLanguage(lang === 'en' ? 'pl' : 'en');
  };

  // Traverses nested locale keys (e.g., t("nav.liveTracker"))
  const t = (path, fallback = "") => {
    const keys = path.split('.');
    let result = translations[lang];
    for (let key of keys) {
      if (result && typeof result === 'object' && key in result) {
        result = result[key];
      } else {
        // Fallback to English if missing in Polish
        let fallbackResult = translations['en'];
        for (let fallbackKey of keys) {
          if (fallbackResult && typeof fallbackResult === 'object' && fallbackKey in fallbackResult) {
            fallbackResult = fallbackResult[fallbackKey];
          } else {
            return fallback || path;
          }
        }
        return fallbackResult;
      }
    }
    return typeof result === 'string' ? result : fallback || path;
  };

  return (
    <LanguageContext.Provider value={{ lang, changeLanguage, setLang: changeLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

// Compact Top Header Flags Component for bilingual theme switches
export const LanguageSwitch = () => {
  const { lang, changeLanguage } = useTranslation();

  return (
    <div className="flex items-center gap-1.5 bg-slate-900/90 border border-slate-700 p-1 rounded-xl shadow-inner shrink-0 select-none">
      <button
        onClick={() => changeLanguage('en')}
        title="Switch to English"
        className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-extrabold transition-all cursor-pointer ${
          lang === 'en'
            ? 'bg-indigo-600 text-white shadow shadow-indigo-500/30'
            : 'text-slate-400 hover:text-slate-200 bg-transparent border-0'
        }`}
      >
        <span className="text-sm">🇬🇧 / 🇺🇸</span>
        <span>EN</span>
      </button>

      <button
        onClick={() => changeLanguage('pl')}
        title="Przełącz na Polski"
        className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-extrabold transition-all cursor-pointer ${
          lang === 'pl'
            ? 'bg-rose-600 text-white shadow shadow-rose-500/30'
            : 'text-slate-400 hover:text-slate-200 bg-transparent border-0'
        }`}
      >
        <span className="text-sm">🇵🇱</span>
        <span>PL</span>
      </button>
    </div>
  );
};
